import { AsyncPipe, NgClass, NgForOf, NgIf, NgSwitch, NgSwitchCase, NgSwitchDefault } from '@angular/common';
import { AfterViewInit, Component, DestroyRef, ElementRef, EventEmitter, inject, Input, NgZone, OnInit, Output, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { EntityTypes } from '@bullhorn/bullhorn-types';
import { combineLatest, Observable, Subject } from 'rxjs';
import { delay, map } from 'rxjs/operators';
import { AdminService } from '../../../../admin/admin.service';
import { FeatureFlagDirective } from '../../../../feature-flags/feature-flag.directive';
import { FeatureFlagService } from '../../../../feature-flags/feature-flag.service';
import { RemoveWhitespace } from '../../../../kyloe-pipes.pipe';
import { DataToolsApps } from '../../../../shared/DataToolsApps';
import { UtilsService } from '../../../../shared/services/utils.service';
import { ActionComponent } from '../../../action/action.component';
import { AddNoteActionComponent } from '../../../action/add-note/add-note-action.component';
import { AiReplaceComponent } from '../../../action/ai-replace/ai-replace.component';
import { AwesomeDocsComponent } from '../../../action/awesome-docs/awesome-docs.component';
import { EnrichActionComponent } from '../../../action/enrich/enrich-action.component';
import { HardDeleteComponent } from '../../../action/hard-delete/hard-delete.component';
import { InsertMultipleActionComponent } from '../../../action/insert-mutliple/insert-multiple-action.component';
import { InsertActionComponent } from '../../../action/insert/insert-action.component';
import { ParseResumeComponent } from '../../../action/parse-resume/parse-resume.component';
import { AiContentDisclaimerComponent } from '../../../ai-content-disclaimer/ai-content-disclaimer.component';
import { AiContentDisclaimerVariant, ruleAiContentDisclaimerVariant } from '../../../ai-content.util';
import { CriteriaComponent } from '../../../criteria/criteria.component';
import { DataRulesConfirmDialogComponent } from '../../../data-rules-confirm-dialog/data-rules-confirm-dialog.component';
import { DataRulesDataService } from '../../../data-rules-data.service';
import {
  ActionData,
  ActionsOperators,
  AddNoteActionValue,
  AllPrimaryEntitiesNames,
  BullhornFieldType,
  CriteriaData,
  IDataRule,
  IFormActionData,
  InsertActionData,
  RawActionData,
  RuleData
} from '../../../data-rules-model';
import { ISavedDataRuleEventData } from '../../../data-rules.component';
import { DataRulesService } from '../../../data-rules.service';
import { ErrorNotificationService } from '../../../error-notification.service';
import { ExamplesFoundComponent } from '../../../examples-found/examples-found.component';
import { IActionFieldMeta, MetaDataService } from '../../../meta-data.service';
import { transitionNewHeightInOut, transitionNewRowInOut, transitionNewWidthInOut } from '../../../rule/kyloe-animations';
import { RuleChainsDataService } from '../../../rule/rule-chain/rule-chain-data.service';
import { ScheduleData, ScheduleDataService } from '../../../rule/rule-schedule/schedule-data.service';

@Component({
  selector: 'app-rule-folders',
  templateUrl: './rule.component.html',
  styleUrls: ['./rule.component.scss'],
  animations: [transitionNewWidthInOut, transitionNewHeightInOut, transitionNewRowInOut],
  imports: [
    AiContentDisclaimerComponent,
    CriteriaComponent,
    ExamplesFoundComponent,
    NgClass,
    ReactiveFormsModule,
    RemoveWhitespace,
    NgIf,
    NgForOf,
    MatExpansionModule,
    MatIconModule,
    MatTooltip,
    AsyncPipe,
    AddNoteActionComponent,
    InsertActionComponent,
    InsertMultipleActionComponent,
    NgSwitchCase,
    AiReplaceComponent,
    ActionComponent,
    HardDeleteComponent,
    NgSwitchDefault,
    NgSwitch,
    AddNoteActionComponent,
    NgSwitch,
    AwesomeDocsComponent,
    FeatureFlagDirective,
    EnrichActionComponent,
    ParseResumeComponent
  ]
})

export class RuleFoldersComponent implements OnInit, AfterViewInit {
  @ViewChild('focusElement') focusElement: ElementRef;
  @ViewChild('actionsList') private actionsList: ElementRef;
  @ViewChild('actionsHeading') private actionsHeadingRef: ElementRef<HTMLElement>;

  @Input() dataRule: IDataRule;
  @Input() ruleIndex: number;
  @Input() currentRuleExamples;
  @Input() selectedEntity: AllPrimaryEntitiesNames;
  @Input() tasksRunning;
  @Input() entityMap;
  @Input() savedRuleEvent: Subject<ISavedDataRuleEventData>;
  @Input() saveTriggered: Subject<void>;
  @Input() nameControl: FormControl | undefined;
  @Input() updateLimitControl: FormControl<number | null> | undefined;

  // only used for data reset
  @Output() currentRuleExamplesChange = new EventEmitter<null>();
  @Output() ruleSavedEmitter = new EventEmitter<boolean>();
  @Output() ruleClickEventEmitter = new EventEmitter<void>();
  @Output() canSaveChange = new EventEmitter<boolean>();
  @Output() isDirtyChange = new EventEmitter<boolean>();

  actionOperators = ActionsOperators;
  dataToolsApps = DataToolsApps;
  form: FormGroup;
  criteria: FormArray;
  actions: FormArray;
  actionMeta: IActionFieldMeta[];
  showNameInput = false;
  isHardDelete: FormControl; // why is this a form??
  disableSaveButton = false;
  aiContentDisclaimerVariant: AiContentDisclaimerVariant | null = null;
  actionsExpanded = false;
  showViewActionsLink = false;
  private destroyRef = inject(DestroyRef);
  private zone = inject(NgZone);
  private contentResizeObserver: ResizeObserver;
  private scrollContainer: HTMLElement;

  constructor(private formBuilder: FormBuilder,
              private metaDataService: MetaDataService,
              protected dataRulesService: DataRulesService,
              private dataRulesDataService: DataRulesDataService,
              private ruleChainDataService: RuleChainsDataService,
              private scheduleDataService: ScheduleDataService,
              private errorNotificationService: ErrorNotificationService,
              private dialog: MatDialog,
              private adminService: AdminService,
              public hostElementRef: ElementRef,
              public utilsService: UtilsService
  ) {
  }

 get scheduleData(): ScheduleData | null {
    const ruleId = this.dataRule.ruleData.id;
    if (this.utilsService.compareInternalDataRuleIds(ruleId, 0))
      return null;
    return this.scheduleDataService.getScheduleByRuleId(ruleId as string);
 }

  ngOnInit(): void {
    this.metaDataService.getActionMeta(this.dataRule.ruleData.source.config.entity).subscribe(meta => this.actionMeta = meta);

    this.showNameInput = this.dataRule.ruleData.name.length === 0;

    this.criteria = this.formBuilder.array([]);
    for (let i = 0; i < this.dataRule.ruleData.source.config.criteria.length; i++)
      this.criteria.push(this.formBuilder.group({}));   // TODO: could we not do something here and get rid of stale actionData input data?

    this.actions = this.formBuilder.array([], this.minActionsValidator());
    const steps = this.dataRule.ruleData.action.config?.steps ?? [];
    for (let i = 0; i < steps.length; i++)
      this.actions.push(this.formBuilder.group({})); // TODO: could we not do something here and get rid of stale actionData input data?

    const isHardDelete = steps.length > 0
      && steps.reduce((acc, val) => {
        return val.type === this.dataRulesService.actionOpName(this.dataRulesService.actionOperators.DELETE)
          ? true : acc;
      }, false);
    this.isHardDelete = this.formBuilder.control(isHardDelete);

    this.form = this.formBuilder.group({
      name: this.nameControl ?? [this.dataRule.ruleData.name, Validators.required],
      criteria: this.criteria,
      actions: this.actions,
      isHardDelete: this.isHardDelete,
      recordUpdateLimit: this.updateLimitControl ?? [this.dataRule.ruleData.recordUpdateLimit ?? null, Validators.min(1)]
    });

    if (this.dataRule.viewState.dirty)
      this.form.markAsDirty();

    this.form.setValidators(this.checkToOneRelationship());

    this.updateAiContentDisclaimerVariant();
  }

  ngAfterViewInit(): void {
    if (this.form.dirty) {
        this.isDirtyChange.emit(true);
        this.canSaveChange.emit(this.form.valid && !this.form.errors);
    }

    this.form
      .valueChanges
      .pipe(delay(50))      // delay because the dirty state is not updated immediately the value changes
      .subscribe(() => {
        this.dataRule.viewState.dirty = this.form.dirty;
        this.disableSaveButton = false;
        const isNewRule = this.utilsService.compareInternalDataRuleIds(this.dataRule.ruleData.id, 0);
        this.canSaveChange.emit(this.form.valid && (isNewRule || this.form.dirty) && !this.form.errors);
        this.isDirtyChange.emit(this.form.dirty);
      });

    if (this.saveTriggered) {
      this.saveTriggered.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.save());
    }

    if (this.showNameInput) {
      this.focusElement?.nativeElement.focus();
    }

    this.observeViewActionsLink();
  }

  private observeViewActionsLink(): void {
    this.scrollContainer = this.hostElementRef.nativeElement.closest('mat-dialog-content');
    if (!this.scrollContainer) {
      return;
    }

    const update = () => this.zone.run(() => {
      const scrollSpaceRemaining = this.scrollContainer.scrollHeight - this.scrollContainer.clientHeight - this.scrollContainer.scrollTop;
      this.showViewActionsLink = scrollSpaceRemaining > 1;
    });

    this.contentResizeObserver = new ResizeObserver(update);
    this.contentResizeObserver.observe(this.hostElementRef.nativeElement);
    this.scrollContainer.addEventListener('scroll', update, {passive: true});
    this.destroyRef.onDestroy(() => {
      this.contentResizeObserver.disconnect();
      this.scrollContainer.removeEventListener('scroll', update);
    });
  }

  scrollToSection(target: 'criteria' | 'actions'): void {
    if (!this.scrollContainer) {
      return;
    }

    if (target === 'criteria') {
      this.scrollContainer.scrollTo({top: 0, behavior: 'smooth'});
    } else {
      const heading = this.actionsHeadingRef.nativeElement;
      const delta = heading.getBoundingClientRect().top - this.scrollContainer.getBoundingClientRect().top;
      this.scrollContainer.scrollTo({top: this.scrollContainer.scrollTop + delta, behavior: 'smooth'});
    }
  }

  onActionsFooterTransitionEnd(event: TransitionEvent): void {
    // Wait for the footer to finish expanding, otherwise there is nothing below the fold to scroll to yet
    if (!this.actionsExpanded || event.propertyName !== 'max-height' || event.target !== event.currentTarget) {
      return;
    }

    (event.currentTarget as HTMLElement).scrollIntoView({behavior: 'smooth', block: 'nearest'});
  }

  get name(): string {
    return this.form.get('name').value;
  }

  toggleView() {
    // TODO: This needs to go - when the Actions preview line is removed
    this.currentRuleExamplesChange.emit(null);
    this.onRuleClick();
  }

  onAppliedToBullhorn(): void {
    this.currentRuleExamplesChange.emit(null);
  }

  parseRawCriteria(criteria: any): Observable<CriteriaData> {
    const {fieldName: rawFieldName, ...rest} = criteria;
    return this.metaDataService.parseFieldNameToDescriptorAndFieldName(rawFieldName).pipe(
      map(({secondaryDescriptor, fieldName}): CriteriaData => ({
        ...rest,
        secondaryDescriptor,
        fieldName
      }))
    );
  }

  parseRawAction(action: RawActionData): Observable<ActionData> {
    const {source: rawSource, destination: rawDestination, ...rest} = action;
    const parsedSource$ = this.metaDataService.parseFieldNameToDescriptorAndFieldName(rawSource);
    const parsedDestination$ = this.metaDataService.parseFieldNameToDescriptorAndFieldName(rawDestination);

    return combineLatest(parsedSource$, parsedDestination$).pipe(
      map(([{
        secondaryDescriptor: sourceDescriptor,
        fieldName: source,
      }, {
        secondaryDescriptor: destinationDescriptor,
        fieldName: destination
      }]) => {
        return ({
          sourceDescriptor, source,
          destinationDescriptor, destination,
          ...rest,
        });
      })
    );
  }

  onNameBlur(): void {
    this.showNameInput = this.name.length === 0;
  }

  onNameClick(): void {
    this.showNameInput = true;
  }

  onNameMouseOver(target): void {
    target.focus();
  }

  addNewCriteria(): void {
    // TODO: This should not be updating the source data, only the form data
    this.dataRule.ruleData.source.config.criteria.push(new CriteriaData('id', 'equals'));
    const newCriteriaGroup = this.formBuilder.group({});
    this.criteria.push(newCriteriaGroup);
    newCriteriaGroup.markAsDirty();
  }

  removeCriteria(index): void {
    if (!this.form.dirty)
      this.form.markAsDirty();
    if (this.criteria.length > 1) {
      this.criteria.removeAt(index);
      // TODO: This should not be updating the source data, only the form data
      this.dataRule.ruleData.source.config.criteria.splice(index, 1);
    }
  }

  addNewAction(): void {
    // TODO: This should not be updating the source data, only the form data
    this.dataRule.ruleData.action.config.steps.push(new ActionData(undefined));
    const newActionGroup = this.formBuilder.group({});
    this.actions.push(newActionGroup);
    newActionGroup.markAsDirty();
    this.actionsExpanded = false;
    this.scrollActionsToBottom();
  }

  addNewInsertCredentialsRequirementAction(): void {
    // todo hardcoded
    const entitySourceType = 'com.bullhornsdk.data.model.entity.core.standard.Certification';
    // todo hardcoded
    let entDestType;
    if (this.selectedEntity === 'Candidate') {
      entDestType = 'com.bullhornsdk.data.model.entity.core.certificationrequirement.CandidateCertificationRequirement';
    } else if (this.selectedEntity === 'Placement') {
      entDestType = 'com.bullhornsdk.data.model.entity.core.standard.PlacementCertification';
    } else if (this.selectedEntity === 'JobSubmission') {
      entDestType = 'com.bullhornsdk.data.model.entity.core.certificationrequirement.JobSubmissionCertificationRequirement';
    }
    const entityDestinationType = entDestType ;
    const destinationType = BullhornFieldType.CERTIFICATION;
    // TODO: This should not be updating the source data, only the form data
    this.dataRule.ruleData.action.config.steps.push(new InsertActionData({entitySourceType, entityDestinationType}, destinationType));
    const newActionGroup = this.formBuilder.group({});
    this.actions.push(newActionGroup);
    newActionGroup.markAsDirty();
    this.actionsExpanded = false;
    this.scrollActionsToBottom();
  }

  addNewInsertMultipleCredentialsRequirementAction(): void {
    const destinationType = BullhornFieldType.CERTIFICATION_GROUP;
    const entitySourceType = '';
    const entityDestinationType = '';
    // TODO: This should not be updating the source data, only the form data
    this.dataRule.ruleData.action.config.steps.push(new InsertActionData({entitySourceType, entityDestinationType}, destinationType, 'INSERT_MULTIPLE'));
    const newActionGroup = this.formBuilder.group({});
    this.actions.push(newActionGroup);
    newActionGroup.markAsDirty();
    this.actionsExpanded = false;
    this.scrollActionsToBottom();
  }

  addNewAIAction(): void {
    this.dataRule.ruleData.action.config.steps.push(new ActionData(ActionsOperators.AIASSIST));
    const newActionGroup = this.formBuilder.group({});
    this.actions.push(newActionGroup);
    newActionGroup.markAsDirty();
    this.actionsExpanded = false;
    this.scrollActionsToBottom();
    this.updateAiContentDisclaimerVariant();
  }

  addNewAddNoteAction(): void {
    const destinationType = BullhornFieldType.NOTE;
    const noteValue: AddNoteActionValue = {
      action: '',
      comments: '',
      isDeleted: false,
      commentsMode: 'SPECIFY'
    };
    // TODO: This should not be updating the source data, only the form data
    this.dataRule.ruleData.action.config.steps.push(new InsertActionData(noteValue, destinationType, 'INSERT'));
    const newActionGroup = this.formBuilder.group({});
    this.actions.push(newActionGroup);
    newActionGroup.markAsDirty();
    this.actionsExpanded = false;
    this.scrollActionsToBottom();
  }

  addNewAwesomeDocsAction(): void {
    this.dataRule.ruleData.action.config.steps.push(new ActionData(ActionsOperators.AWESOMEDOCS));
    const newActionGroup = this.formBuilder.group({});
    this.actions.push(newActionGroup);
    newActionGroup.markAsDirty();
    this.actionsExpanded = false;
    this.scrollActionsToBottom();
    this.updateAiContentDisclaimerVariant();
  }

  removeAction(index): void {
    if (!this.form.dirty)
      this.form.markAsDirty();
    this.actions.removeAt(index);
    // TODO: This should not be updating the source data, only the form data
    this.dataRule.ruleData.action.config.steps.splice(index, 1);
    this.updateAiContentDisclaimerVariant();
  }

  setHardDelete(isSet: boolean): void {
    this.isHardDelete.setValue(isSet);

    if (isSet) {
      this.actions.clear();
      // TODO: This should not be updating the source data, only the form data
      this.dataRule.ruleData.action.config.steps = [new ActionData('DELETE')];
      this.actions.push(this.formBuilder.group({}));
      this.form.markAsDirty();
      this.actionsExpanded = false;
      this.scrollActionsToBottom();
    } else {
      this.actions.removeAt(0);
      // TODO: This should not be updating the source data, only the form data
      this.dataRule.ruleData.action.config.steps = [];
    }
    this.updateAiContentDisclaimerVariant();
  }

  private updateAiContentDisclaimerVariant(): void {
    this.aiContentDisclaimerVariant = this.adminService.checkAppEnabled(DataToolsApps.DATARULES_AI_ASSIST)
      ? ruleAiContentDisclaimerVariant(this.dataRule)
      : null;
  }

  private scrollActionsToBottom(): void {
    setTimeout(() => {
      const items = this.actionsList.nativeElement.querySelectorAll('li');
      items[items.length - 1]?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
  }

  save(): void {
    const chain = this.ruleChainDataService.getChainByRuleId(this.dataRule.ruleData.id);
    if (!!this.scheduleData || !!chain) {
      let dialogTitle: string;
      if (!!this.scheduleData && !!chain)
        dialogTitle = 'This rule is scheduled and in a chain, are you sure you want to save changes?';
      else if (!!this.scheduleData && !chain)
        dialogTitle = 'This rule is scheduled, are you sure you want to save changes?';
      else if (!this.scheduleData && !!chain )
        dialogTitle = 'This rule is in a chain, are you sure you want to save changes?';

      const dialogRef = this.dialog.open(DataRulesConfirmDialogComponent, {
        width: '45vw',
        height: 'auto',
        autoFocus: true,
        data: {
          titleIcon: 'bhi-delete-o',
          title: dialogTitle,
          messageIcon: 'bhi-search',
          message: this.dataRule.ruleData.name
        }
      });

      dialogRef.afterClosed().subscribe((data) => {
        if (!data)
          return;
        this.saveRule();
      });
    } else {
      this.saveRule();
    }
  }

  saveRule() {
    // clone the ruleData in case the save fails
    const saveRule = RuleData.clone(this.dataRule.ruleData);

    saveRule.name = this.form.value.name;
    saveRule.recordUpdateLimit = this.form.value.recordUpdateLimit || null;
    const resultCriteriaData = [];
    this.form.value.criteria.forEach((cr: CriteriaData) => {
      resultCriteriaData.push(CriteriaData.serialize(cr));
    });

    saveRule.source.config.criteria = resultCriteriaData;

    const resultActionSteps: ActionData[] = [];
    this.form.getRawValue().actions.forEach((ac: IFormActionData) => {
      const {
        sourceDescriptor, source,
        destinationDescriptor, destination,
        value,
        entitySourceType, entityDestinationType, sourceId,
        action, comments, isDeleted, commentsMode,
        resumeType, fieldToPopulateWithResume, fieldToPopulateWithResumeDate,
        ...rest
      } = ac;
      let resultValueObj;
      if (ac.type === this.dataRulesService.actionOpName(this.dataRulesService.actionOperators.INSERT) ||
        ac.type === this.dataRulesService.actionOpName(this.dataRulesService.actionOperators.INSERT_MULTIPLE)) {
        // Check if this is a Note action (destinationType === NOTE)
        if (ac.destinationType === BullhornFieldType.NOTE) {
          resultValueObj = {
            value: {
              action,
              comments,
              isDeleted,
              commentsMode
            }
          };
        } else {
          // Certification action
          resultValueObj = {
            value: {
              entitySourceType,
              entityDestinationType,
              sourceId
            }
          };
        }
      } else if (ac.type === this.dataRulesService.actionOpName(this.dataRulesService.actionOperators.PARSE_RESUME)) {
        resultValueObj = {
          value: {
            resumeType,
            fieldToPopulateWithResume,
            fieldToPopulateWithResumeDate
          }
        };
      } else {
        resultValueObj = {
          value: ac.value
        };
      }
      resultActionSteps.push({
        source: sourceDescriptor ? `${sourceDescriptor}.${source}` : source,
        destination: destinationDescriptor ? `${destinationDescriptor}.${destination}` : destination,
        ...rest,
        ...resultValueObj
      });
    });

    saveRule.action.config.steps = resultActionSteps;
    this.disableSaveButton = true;
    this.canSaveChange.emit(false);
    this.ruleSavedEmitter.emit(false);

    this.dataRulesDataService.saveRule(saveRule).pipe(
      map(id => {
        return this.dataRulesDataService.getRule(id);
      })
    ).subscribe(savedRule => {
      console.log('Saved:', savedRule);
      const unsavedId = this.dataRule.ruleData.id;
      this.dataRule.ruleData = savedRule;       // TODO: this is a fudge to make the UI work
                                                // (by keeping the criteria and actions used in the child components up-to-date)
                                                // because of reliance on non-form model
      this.dataRule.viewState.dirty = false;
      this.form.markAsPristine();
      this.showNameInput = false;
      this.isDirtyChange.emit(false);
      this.ruleSavedEmitter.emit(true);
      this.savedRuleEvent.next({unsavedId, savedId: savedRule.id as string});

    },
      error => {
        this.errorNotificationService.error('Failed to save', error);
      }
    );
  }

  minActionsValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const formArray = control as FormArray;
      return formArray.length > 0 ? null : {minActions: {message: 'At least one action required'}};
    };
  }

  checkToOneRelationship(): ValidatorFn {
    return (control: FormGroup): { [key: string]: any } | null => {
      const formGroup = control as FormGroup;
      const actions = (formGroup.get('actions') as FormArray).getRawValue();
      const toOnes = actions.filter(a => a.destinationType === 'TO_ONE' && a.type === 'CHANGE');
      let toOneErrors = [];

      toOnes.forEach(toOne => {
        console.log(toOne);

        // look for other actions where the destinationDescriptor is the is a.destination
        // this is check that we aren't changing a field on an association, where we are also trying to change the association itself
        const ers = actions.filter(ae =>
          (ae.destinationDescriptor === toOne.destination || ae.sourceDescriptor === toOne.destination)
          && ae.destinationType !== 'TO_ONE'
          && ae.type !== 'CHANGE'
        );
        if (ers.length > 0) {
          console.log(ers);
          toOneErrors.push('Unable to change ' + toOne.destination + ' association and a field on same entity');
        }

        // this is check that we aren't attempting to change the same TO_ONE association twice to two different values
        const actins = actions.filter(ae =>
          ae.destination === toOne.destination
          && ae.value !== toOne.value
          && ae.destinationType === 'TO_ONE'
          && ae.type === 'CHANGE'
        );
        if (actins.length > 0) {
          console.log(actins);
          toOneErrors.push('Unable to change ' + toOne.destination + ' association to two different values');
        }
      });
      toOneErrors = toOneErrors.filter(function (elem, index, self) {
        return index === self.indexOf(elem);
      });
      return toOneErrors.length === 0 ? null : {toOnes: {message: toOneErrors.join(',')}};
    };
  }

  canSpawnAddActionButton(): boolean {
    return !this.isHardDelete.value;
  }

  canSpawnHardDeleteButton(): boolean {
    // todo optimise on KENG-107 implementation
    return this.selectedEntity === 'PlacementCommission';
  }

  canSpawnAddAIActionButton(): boolean {
    const allowedEntities: AllPrimaryEntitiesNames[] = [EntityTypes.Candidate,
      EntityTypes.ClientContact,
      EntityTypes.JobOrder,
      EntityTypes.ClientCorporation,
      EntityTypes.Opportunity,
      EntityTypes.Placement,
      EntityTypes.Lead,
      EntityTypes.JobSubmission];

    return allowedEntities.includes(this.selectedEntity)
      && this.adminService.checkAppEnabled(DataToolsApps.DATARULES_AI_ASSIST);
  }

  canSpawnAddAwesomeDocsButton(): boolean {
    const allowedEntities: AllPrimaryEntitiesNames[] = [
      EntityTypes.Candidate,
      EntityTypes.ClientContact,
      EntityTypes.JobOrder,
      EntityTypes.JobSubmission,
      EntityTypes.Opportunity,
      EntityTypes.Placement
    ];
    return allowedEntities.includes(this.selectedEntity);
  }

  canSpawnCredentialRequirementButton(): boolean {
    return (this.selectedEntity === EntityTypes.Candidate
        || this.selectedEntity === EntityTypes.Placement
        || this.selectedEntity === EntityTypes.JobSubmission)
      && this.adminService.checkAppEnabled(DataToolsApps.DATARULES_CREDENTIALLING);
  }

  canSpawnMultipleCredentialRequirementButton(): boolean {
    return this.canSpawnCredentialRequirementButton();
  }

  canSpawnAddNoteButton(): boolean {
    const allowedEntities: AllPrimaryEntitiesNames[] = [
      EntityTypes.Candidate,
      EntityTypes.ClientContact,
      EntityTypes.Lead,
      EntityTypes.JobOrder,
      EntityTypes.JobSubmission,
      EntityTypes.Placement,
      EntityTypes.Opportunity,
    ];
    return allowedEntities.includes(this.selectedEntity);
  }

  canSpawnEnrichButton(): boolean {
    const allowedEntities: AllPrimaryEntitiesNames[] = [
      EntityTypes.Candidate,
      EntityTypes.ClientContact,
    ];
    return allowedEntities.includes(this.selectedEntity);
  }

  canSpawnParseResumeButton(): boolean {
    return this.selectedEntity === EntityTypes.Candidate;
  }

  addNewEnrichAction(): void {
    this.dataRule.ruleData.action.config.steps.push(new ActionData(ActionsOperators.ENRICH));
    const newActionGroup = this.formBuilder.group({});
    this.actions.push(newActionGroup);
    newActionGroup.markAsDirty();
  }

  addNewParseResumeAction(): void {
    this.dataRule.ruleData.action.config.steps.push(new ActionData(ActionsOperators.PARSE_RESUME));
    const newActionGroup = this.formBuilder.group({});
    this.actions.push(newActionGroup);
    newActionGroup.markAsDirty();
  }

  onRuleClick() {
    this.ruleClickEventEmitter.emit();
  }

  protected readonly FeatureFlagService = FeatureFlagService;
}
